import { motion } from "framer-motion";
import { Crown } from "lucide-react";

const MAIN_SPONSORS = [
  { name: "S Properties", src: "/partners/sproperties.jpg" },
  { name: "ERA Real Estate", src: "/partners/era.png" },
  { name: "NEW TOWER", src: "/partners/newtower.png", dark: true },
];

const OTHER_PARTNERS = [
  { name: "КСБ · Варна", src: "/partners/ksb.png", role: "Партньор" },
  { name: "Credit Zone", src: "/partners/kz.png", role: "Спонсор" },
  { name: "BIGLA III", src: "/partners/bigla.png", role: "Спонсор" },
  { name: "LEX BUILD", src: "/partners/lexbuild.webp", role: "Спонсор" },
  { name: "Изолации ЕООД", src: "/partners/izolatsii.png", role: "Партньор" },
  { name: "Altcoins.bg", src: "/partners/altcoins.png", role: "Спонсор", dark: true },
  { name: "VivEra Design Studio", src: "/partners/vivera.png", role: "Партньор" },
  { name: "Топливо АД", src: "/partners/toplivo.png", role: "Партньор" },
];

export default function Sponsors() {
  return (
    <section
      data-testid="sponsors-section"
      className="relative py-20 sm:py-28 border-t border-[#F5EBD9]/10 bg-[#0B1F3D] text-[#F5EBD9] overflow-hidden"
    >
      <div className="absolute inset-0 sunset-glow opacity-30 pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        {/* MAIN SPONSOR — UniCredit Bulbank */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
          data-testid="main-sponsor"
        >
          <div className="inline-flex items-center gap-2 bg-[#F5892E] text-[#0B1F3D] px-4 py-2 mb-8">
            <Crown className="w-4 h-4" strokeWidth={2.5} />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] font-semibold">
              Главен спонсор
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative mx-auto max-w-3xl"
          >
            <div className="relative bg-white p-10 sm:p-14 lg:p-16 frame-shadow flex items-center justify-center">
              <img
                src="/partners/unicredit.png"
                alt="UniCredit Bulbank — главен спонсор"
                data-testid="unicredit-logo"
                className="relative max-h-32 sm:max-h-40 lg:max-h-48 w-auto object-contain"
              />
            </div>
            <div className="mt-6 font-serif text-lg sm:text-xl text-[#F5EBD9]/85">
              С подкрепата на <span className="font-display text-[#F5EBD9]">УниКредит Булбанк</span> —
              главен спонсор на събитието
            </div>
          </motion.div>
        </motion.div>

        {/* MAIN SPONSORS — S Properties & ERA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
          data-testid="main-sponsors"
        >
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#F5892E]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              Главни спонсори
            </span>
            <span className="h-px w-10 bg-[#F5892E]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {MAIN_SPONSORS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                data-testid={`main-sponsor-${i}`}
                className={`${p.dark ? "bg-[#0F2647] border border-[#F5EBD9]/15" : "bg-white"} p-8 sm:p-10 frame-shadow flex items-center justify-center min-h-[160px] sm:min-h-[180px]`}
              >
                <img
                  src={p.src}
                  alt={`${p.name} — главен спонсор`}
                  className="max-h-20 sm:max-h-24 w-auto object-contain"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* MEDIA PARTNER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="max-w-md mx-auto text-center mb-16 sm:mb-20"
          data-testid="special-partners"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/80 border border-[#F5EBD9]/30 px-3 py-1.5 rounded-full">
              Медиен партньор
            </span>
          </div>
          <div className="bg-white p-8 sm:p-10 frame-shadow border border-[#F5EBD9]/15 flex items-center justify-center min-h-[140px] sm:min-h-[160px]">
            <img
              src="/partners/varnensko-utro.png"
              alt="Варненско Утро — медиен партньор"
              data-testid="varnensko-utro-logo"
              className="max-h-24 sm:max-h-28 w-auto object-contain"
            />
          </div>
        </motion.div>

        {/* Other partners */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 pt-10 border-t border-[#F5EBD9]/15"
        >
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5EBD9]/30" />
            <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
              Партньори и спонсори
            </span>
            <span className="h-px w-10 bg-[#F5EBD9]/30" />
          </div>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-px bg-[#F5EBD9]/10">
          {OTHER_PARTNERS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              data-testid={`partner-${i}`}
              className={`${p.dark ? "bg-[#020404]" : "bg-white"} aspect-[3/2] flex flex-col items-center justify-center p-4 relative`}
            >
              <img
                src={p.src}
                alt={p.name}
                className="max-h-[75%] max-w-[88%] w-auto object-contain"
              />
              <div className={`mt-2 font-mono text-[9px] uppercase tracking-[0.24em] ${p.dark ? "text-[#F5EBD9]/60" : "text-[#0B1F3D]/60"}`}>
                {p.role}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
